'use client';

import React, { useContext, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import type { Source } from '@/shared/types';
import { SourceCard } from './sourceCard';
import { CsamDetectionPill } from './ui/media/csam-detection-pill';
import type { EventFromChat } from './answer-item';
import { FullScreenContext } from '@/app/contexts';
import { useCsamAccess } from '@/lib/csam-access';
import {
  toDetectionType,
  useContentModerationImageDescription,
} from '@/hooks/useContentModerationImageDescription';
import { toBypassCmPreviewSource } from './evidence-locker-ui-shared-new';

/**
 * Clearance under ``#main-navigation`` — keep in sync with main content offsets
 * (`top-[56px] sm:top-[67px]` in ``app/main.tsx``).
 */
export const MAIN_NAV_BELOW_CLASS = 'top-[56px] sm:top-[67px]';

/** Safe band below the nav where evidence locker / preview panels may paint. */
export const BELOW_MAIN_NAV_SAFE_AREA_CLASS =
  `fixed inset-x-0 bottom-0 ${MAIN_NAV_BELOW_CLASS}`;

/** Matches ``EvidenceLockerModalNew`` shell dimensions (desktop), capped to space below nav. */
export const EVIDENCE_LOCKER_SHELL_SIZE_CLASS =
  'w-[min(95vw,920px)] max-h-full h-[min(90vh,820px)]';

/**
 * Preview dialog surface: fills the safe area on mobile; locker-sized and centered
 * within the safe area on sm+ (never overlaps NavigationBarHorizontal).
 */
export const EVIDENCE_LOCKER_PREVIEW_DIALOG_CLASS =
  'box-border flex h-full max-h-full w-full max-w-[100vw] flex-col overflow-hidden rounded-none bg-[#2C3A4A] shadow-xl ' +
  'sm:h-[min(90vh,820px)] sm:w-[min(95vw,920px)] sm:max-w-[min(95vw,920px)] sm:rounded-lg';

export type EvidenceLockerPreviewOverlayProps = {
  isOpen: boolean;
  url: string;
  pageCount?: number;
  /** CM detection type — when set the Obfuscated/Original toggle is shown. */
  contentModerationType?: string | null;
  /** Obfuscated preview URL (the CM-safe thumbnail). */
  previewUrl?: string | null;
  /** Human-readable filename shown in the SourceCard header. */
  label?: string;
  /** Full chat citation identity — preserves dispatched/searchable paths for container extracts. */
  citationSource?: Source;
  /** Whether the obfuscated (safe) view is active; defaults to true. */
  showObfuscatedPreview?: boolean;
  /** Called when the user switches between obfuscated and original views. */
  onToggleObfuscatedPreview?: (show: boolean) => void;
  caseId: string;
  previewRef?: React.Ref<HTMLDivElement | null>;
  onClose: () => void;
  handleDownload: (source: Source) => void | Promise<void>;
  handleAddSourceToScope: (source: Source) => void;
  handleCopySourceForTimeline: (source: Source, index: number) => void | Promise<void>;
  handleCopyToMediaShare?: (
    source: Source,
    index: number,
    onSuccess?: () => void,
  ) => void;
  copiedSource: number;
  addedSource: number;
  /** Omit on surfaces with no timeline (HelioTrace); the create-event actions are then hidden. */
  addToTimeline?: (event: EventFromChat) => void;
  /** Stacking class. Default sits in the chat locker band; raise above theme Modal (z-100000) when nested. */
  zIndexClass?: string;
};

export function EvidenceLockerPreviewOverlay({
  isOpen,
  url,
  pageCount,
  contentModerationType,
  previewUrl,
  label,
  citationSource,
  showObfuscatedPreview = true,
  onToggleObfuscatedPreview,
  caseId,
  previewRef,
  onClose,
  handleDownload,
  handleAddSourceToScope,
  handleCopySourceForTimeline,
  handleCopyToMediaShare,
  copiedSource,
  addedSource,
  addToTimeline,
  zIndexClass = 'z-[10053]',
}: EvidenceLockerPreviewOverlayProps) {
  // Master gate for the CSAM image-preview feature (label + obfuscated preview).
  const { canSeeCsamTags, canSeeDescriptions, canRevealOriginal } = useCsamAccess();
  const { isFullscreen } = useContext(FullScreenContext);
  const hasCm = isOpen && canSeeCsamTags && !!contentModerationType;
  const detectionType = toDetectionType(contentModerationType);
  const embeddedImageDescription =
    citationSource?.fileInfo?.contentModeration?.imageDescription ?? null;
  const needsFindingDescription =
    hasCm &&
    canSeeDescriptions &&
    Boolean(detectionType) &&
    !embeddedImageDescription?.trim();
  const findingDescriptionQuery = useContentModerationImageDescription(caseId, {
    detectionType,
    fileMd5: citationSource?.fileInfo?.md5 ?? null,
    filePath: citationSource?.sourceFilePath ?? url,
    enabled: needsFindingDescription,
  });
  const contentModerationImageDescription = useMemo(() => {
    if (embeddedImageDescription?.trim()) return embeddedImageDescription.trim();
    return findingDescriptionQuery.data ?? null;
  }, [embeddedImageDescription, findingDescriptionQuery.data]);
  // Not ``isPending``: the query is disabled until the case's folder segment
  // resolves, and a disabled TanStack query stays ``isPending`` forever — so a
  // case that can never resolve (cases query errored, WAF-blocked, or absent
  // from the permissioned list) would spin here instead of settling.
  const contentModerationImageDescriptionPending =
    needsFindingDescription &&
    (findingDescriptionQuery.isCaseResolving || findingDescriptionQuery.isFetching);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || isFullscreen) return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, onClose, isFullscreen]);

  if (!isOpen || typeof document === 'undefined' || !url) return null;

  // Non-reviewers can never leave the obfuscated view.
  const effectiveShowObfuscated = canRevealOriginal ? showObfuscatedPreview : true;

  const cmHeaderPrefix = hasCm ? (
    contentModerationType ? <CsamDetectionPill type={contentModerationType} /> : undefined
  ) : undefined;

  const overlaySource: Source = citationSource
    ? {
        ...citationSource,
        pageCount: pageCount ?? citationSource.pageCount ?? undefined,
        ...(label ? { label } : {}),
      }
    : {
        sourceUrl: url,
        sourceFilePath: url,
        pageCount: pageCount ?? undefined,
        ...(label ? { label } : {}),
      };

  const previewSource: Source = hasCm && effectiveShowObfuscated
    ? { ...overlaySource, previewFilePath: previewUrl ?? undefined }
    : hasCm && !effectiveShowObfuscated
      ? toBypassCmPreviewSource(overlaySource)
      : overlaySource;

  const sharedSourceCardProps = {
    fillScrollport: true,
    selectedSourceIndex: 0,
    answerIndex: 0,
    epochTimeSecs: 0,
    handleDownload,
    handleAddSourceToScope: (s: Source) => { onClose(); handleAddSourceToScope(s); },
    handleCopySourceForTimeline,
    handleCopyToMediaShare,
    setIsExpanded: () => {},
    copiedSource,
    addedSource,
    closeSources: onClose,
    addToTimeline,
    caseId,
    headerPrefix: cmHeaderPrefix,
    showObfuscatedPreview: hasCm ? effectiveShowObfuscated : undefined,
    onToggleObfuscatedPreview: hasCm && canRevealOriginal ? onToggleObfuscatedPreview : undefined,
    contentModerationType: hasCm ? (contentModerationType ?? undefined) : undefined,
    contentModerationImageDescription: hasCm && canSeeDescriptions ? contentModerationImageDescription : undefined,
    contentModerationImageDescriptionPending: hasCm
      ? contentModerationImageDescriptionPending
      : undefined,
    sources: [previewSource],
    fullScreenSource: hasCm ? undefined : overlaySource,
  };

  return createPortal(
    // Safe area below NavigationBarHorizontal — preview is centered/resized inside
    // this region so it never paints under the fixed nav (z-index wins for the nav).
    <div
      className={`${BELOW_MAIN_NAV_SAFE_AREA_CLASS} ${zIndexClass} flex items-center justify-center sm:p-3 pointer-events-none${isFullscreen ? ' invisible' : ''}`}
      aria-hidden={isFullscreen || undefined}
    >
      <div
        ref={previewRef as React.Ref<HTMLDivElement> | undefined}
        role="dialog"
        aria-modal="true"
        aria-label="Evidence preview"
        className={`pointer-events-auto ${EVIDENCE_LOCKER_PREVIEW_DIALOG_CLASS}`}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="custom-scrollbar2 flex min-h-0 flex-1 flex-col overflow-hidden">
          <div
            data-source-card-scrollport
            data-evidence-locker-overlay
            className="flex min-h-0 h-full w-full flex-1 flex-col overflow-y-auto pt-[10px] pb-[20px] custom-scrollbar2"
          >
          <SourceCard
            key={hasCm ? (effectiveShowObfuscated ? 'obf' : 'orig') : 'plain'}
            {...sharedSourceCardProps}
          />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
